import { DqdlRule, RuleType } from "./types"

/**
 * Suggestions data structure from the API, containing AI-generated rules split by severity.
 */
export interface SuggestionsData {
  majorRules: DqdlRule[]
  minorRules: DqdlRule[]
}

/**
 * Rule types that use WHERE-clause-based duplicate detection. For these types, two rules on the
 * same column with different WHERE clauses are considered distinct (targeting different
 * conditions).
 */
const WHERE_AWARE_TYPES: ReadonlySet<RuleType> = new Set([
  "IsComplete",
  "Completeness",
  "DataFreshness",
  "DistinctValuesCount",
])

/**
 * Returns true if the WHERE clause is effectively absent (null, undefined, or empty string).
 */
function hasNoWhere(rule: DqdlRule): boolean {
  return !rule.where
}

/**
 * Checks if a suggestion is subsumed by derived rules (metadata-based, unconditional).
 *
 * A derived rule subsumes a suggestion of the same type on the same column: derived IsComplete,
 * ColumnLength, and ColumnDataType each subsume any suggestion of their type on that column, and
 * (since a derived IsComplete is unconditional) it also subsumes a Completeness suggestion at any
 * threshold. Derived IsPrimaryKey subsumes any IsPrimaryKey regardless of columns.
 */
function isSubsumedByDerived(suggestion: DqdlRule, derived: DqdlRule[]): boolean {
  if (suggestion.type === "CustomSql") return false

  if (suggestion.type === "IsPrimaryKey") {
    return derived.some((r) => r.type === "IsPrimaryKey")
  }

  // A derived IsComplete is unconditional, so it also covers a Completeness suggestion.
  const derivedType = suggestion.type === "Completeness" ? "IsComplete" : suggestion.type
  return derived.some((r) => r.type === derivedType && r.column === suggestion.column)
}

/**
 * Checks if a suggestion is a duplicate of an existing form rule.
 *
 * Matching logic varies by type: - IsPrimaryKey: any existing IsPrimaryKey blocks any suggestion
 * (one PK per dataset) - ColumnLength/ColumnValues: same type + same column = duplicate (no WHERE
 * logic) - WHERE-aware types: same type + same column, then WHERE clause comparison determines
 * whether the suggestion is genuinely different - Cross-type: existing unconditional IsComplete
 * subsumes Completeness suggestion - CustomSql: never filtered.
 */
function isDuplicateOfExisting(suggestion: DqdlRule, existingRules: DqdlRule[]): boolean {
  if (suggestion.type === "CustomSql") return false

  if (suggestion.type === "IsPrimaryKey") {
    return existingRules.some((r) => r.type === "IsPrimaryKey")
  }

  if (suggestion.type === "ColumnLength") {
    return existingRules.some((r) => r.type === "ColumnLength" && r.column === suggestion.column)
  }

  if (suggestion.type === "ColumnDataType") {
    return existingRules.some((r) => r.type === "ColumnDataType" && r.column === suggestion.column)
  }

  if (suggestion.type === "ColumnValues") {
    return existingRules.some((r) => r.type === "ColumnValues" && r.column === suggestion.column)
  }

  // Cross-type subsumption: existing unconditional IsComplete subsumes Completeness suggestion
  if (suggestion.type === "Completeness") {
    const subsumedByIsComplete = existingRules.some(
      (r) => r.type === "IsComplete" && r.column === suggestion.column && hasNoWhere(r),
    )
    if (subsumedByIsComplete) return true
  }

  // WHERE-aware same-type matching
  if (WHERE_AWARE_TYPES.has(suggestion.type)) {
    return existingRules.some((r) => {
      if (r.type !== suggestion.type || r.column !== suggestion.column) return false

      const existingHasNoWhere = hasNoWhere(r)
      const suggestionHasNoWhere = hasNoWhere(suggestion)

      // Existing unconditional subsumes any suggestion on same type+column
      if (existingHasNoWhere) return true
      // Suggestion is unconditional but existing has WHERE - suggestion is less specific
      if (suggestionHasNoWhere) return true
      // Both have WHERE - duplicate only if identical
      return r.where === suggestion.where
    })
  }

  return false
}

/**
 * Categorizes AI-generated DQ suggestions as "new" or "added" based on derived rules and existing
 * form rules. Returns all suggestions with a status field.
 *
 * @param suggestions - Raw AI suggestions from the API.
 * @param derivedRules - Rules derived from metadata (IsPrimaryKey, IsComplete, ColumnLength,
 *   ColumnDataType)
 * @param existingFormRules - All rules currently in the form (majorRules + minorRules combined)
 * @returns Suggestions with each rule tagged as "new" or "added"
 */
export function filterDuplicateSuggestions(
  suggestions: SuggestionsData,
  derivedRules: DqdlRule[],
  existingFormRules: DqdlRule[],
): SuggestionsData {
  const tagRule = (rule: DqdlRule): DqdlRule => {
    const isAdded =
      isSubsumedByDerived(rule, derivedRules) || isDuplicateOfExisting(rule, existingFormRules)
    return isAdded ? { ...rule, _added: true } : rule
  }

  return {
    majorRules: suggestions.majorRules.map(tagRule),
    minorRules: suggestions.minorRules.map(tagRule),
  }
}
